import React from 'react';
import cn from 'classnames';

import {
  TypographySize,
  TypographyWeight
} from 'widget/shared/ui/Typography/types';

import Typography, { TypographyColor } from '../Typography';

import ErrorReference from './ErrorReference';
import ClearButton from './ClearButton';
import PhoneInput from './PhoneInput/PhoneInput';
import styles from './Input.module.scss';

export type InputType = 'text' | 'email' | 'tel';
export type ValidateMode = 'onBlur' | 'onChange';

export type InputProps = {
  type?: InputType;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  validateMode?: ValidateMode;
  label?: string;
  error?: string | null;
  tooltip?: string | null;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  withClear?: boolean;
  fontSize?: TypographySize;
  fontWeight?: TypographyWeight;
};

const Input: React.FC<InputProps> = ({
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  label,
  validateMode = 'onBlur',
  error = null,
  tooltip = null,
  disabled = false,
  required = false,
  withClear = false,
  fontSize = TypographySize.s,
  fontWeight = TypographyWeight.normal,
  className
}) => {
  const isPhoneInput = type === 'tel';
  const [showError, setShowError] = React.useState(false);
  const hasVisibleError = !!error && showError;
  const showClearButton = withClear && value.length > 0 && !disabled;

  const hasSuffix = hasVisibleError || showClearButton;

  const handleChange = React.useCallback(
    (value: string) => {
      onChange(value);
      if (validateMode === 'onChange') {
        setShowError(true);
      }
    },
    [onChange, validateMode]
  );

  const handleChangeBaseInput = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event.target.value);
    },
    [handleChange]
  );

  const handleBlur = React.useCallback(() => {
    setShowError(true);
    onBlur?.();
  }, [onBlur]);

  const handleFocus = React.useCallback(() => {
    setShowError(false);
  }, []);

  const handleClear = React.useCallback(() => {
    onChange('');
  }, [onChange]);

  const renderSuffix = () => {
    if (!hasSuffix) {
      return null;
    }

    return (
      <div className={styles['input-row__suffix']}>
        {hasVisibleError && <ErrorReference tooltip={tooltip} />}
        {showClearButton && <ClearButton onClick={handleClear} />}
      </div>
    );
  };

  return (
    <div
      className={cn(
        styles.root,
        hasVisibleError && styles.root_error,
        className
      )}
    >
      {label && (
        <label className={styles.label}>
          <Typography
            size={fontSize}
            weight={fontWeight}
            color={TypographyColor.secondary}
          >
            {label}
            {required && <span className={styles.required}> *</span>}
          </Typography>
        </label>
      )}
      {isPhoneInput ? (
        <div className={styles['input-row']}>
          <PhoneInput
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            error={hasVisibleError}
            hasSuffix={hasSuffix}
          />
          {renderSuffix()}
        </div>
      ) : (
        <div className={styles['input-row']}>
          <input
            type={type}
            className={cn(styles.input, hasSuffix && styles.input_withSuffix)}
            value={value}
            onChange={handleChangeBaseInput}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
          />
          {renderSuffix()}
        </div>
      )}
      {hasVisibleError && <span className={styles['error']}>{error}</span>}
    </div>
  );
};

export default React.memo(Input);
